import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { filter, map, Observable, shareReplay, Subject, Subscription, tap, withLatestFrom } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { objectValue, promiseWithCatch, subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { ChatSendMessageToService } from 'src/app/components/chat/services/chat-send-message-to.service';
import { SearchCategories } from 'src/app/components/search/search.enum';
import { UsersSearchService } from 'src/app/components/search/users-search.service';
import { PaginationState } from 'src/app/components/shared/pagination/pagination.interface';
import { CellActionTypes } from 'src/app/components/tables/components/cell-actions.type';
import { RelationRequestTableFactory } from 'src/app/components/user-relation-requests/grid-table-factory/user-relation-requests-table';
import { SearchState, SortingOrderType, SortParams } from 'src/app/interfaces/search.interface';
import { TableCellOnActionEvent as TableEvent, UserTableConstructorInfo } from 'src/app/interfaces/tables.interfaces';
import { MediaQueryService } from 'src/app/services/media-query-service';
import { AccountStatus } from 'src/app/swagger-api';
import { assertNonNullish, MaybeNull, MaybeUndefined } from 'src/app/utils';
import { FilterOption } from 'src/app/views/filter-drop-down/filter-drop-down.component';
import {
  DEFAULT_SORT_OPTIONS,
  NAME_SORT_OPTIONS,
  SortCategories,
  SortOptions,
} from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { ListController, ListMode, ListState, sortParams } from '../../../shared/list-controller/list-controller';
import { UserSearchTableConstructor } from '../../grid-table-factory/search-table';
import { RelationPageTabs } from '../../relation-request.enum';
import {
  ConnectionRequest,
  FilterValue,
  RelationRequests,
  RelationRequestsCount,
  RelationRequestsPageConfig,
  SuggestedUser,
  SuggestedUsers,
  UserBasic,
} from '../../relation-request.interface';
import { RELATION_REQUESTS_PAGE_CONFIG, RELATION_REQUESTS_TABLES } from '../../relation-request.tokens';
import { RelationRequestDialogContextService } from '../../services/relation-request-dialog-context.service';
import { UserRelationRequestService } from '../../services/relation-request.service';
import {
  actionButtonTestId,
  AllowedSearchTableEvent,
  AllowedTableEvent,
  buildSorting,
  RelationRequestPageTabs,
  routeParamToTabId,
  SearchTableAction,
  sortingBy,
  startedUserDialogue,
} from './manage-relation-request.helper';

@Component({
  selector: 'manage-relation-request',
  templateUrl: '../manage-relation-request/manage-relation-request.html',
  styleUrls: ['../manage-relation-request/manage-relation-request.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RelationRequestDialogContextService, UsersSearchService],
})
export class ManageRelationRequestComponent<T extends ConnectionRequest, K extends FilterValue>
  implements OnInit, OnDestroy
{
  public readonly RelationPageTabs: typeof RelationPageTabs = RelationPageTabs;
  public readonly ListMode: typeof ListMode = ListMode;

  public readonly SORT_OPTIONS: SortOptions[] = DEFAULT_SORT_OPTIONS;
  public readonly NAME_SORT_OPTIONS: SortOptions[] = NAME_SORT_OPTIONS;
  public readonly DEBOUNCE_TIME: number = AppValues.DEBOUNCE_TIME;
  public readonly PAGE_LIMIT: number = AppValues.PAGE_LIMIT;
  public readonly LINK_GROUP_MESSAGE: string = ProjectLinks.LINK_GROUP_MESSAGE;

  public readonly isMobile$: Observable<boolean> = inject(MediaQueryService).match$.pipe(shareReplay(1));
  public readonly pageConfig = inject<RelationRequestsPageConfig<K>>(RELATION_REQUESTS_PAGE_CONFIG);

  public readonly isLoading$: Observable<boolean>;
  public readonly tableData$: Observable<UserTableConstructorInfo>;
  public readonly totalTabItems$: Observable<RelationRequestsCount>;
  public readonly searchTableData$: Observable<UserTableConstructorInfo>;

  private readonly tableAction$: Subject<TableEvent> = new Subject();
  private readonly searchTableAction$: Subject<TableEvent> = new Subject();

  private readonly activeTab: ListController<RelationPageTabs, K> = new ListController<RelationRequestPageTabs, K>(
    this.pageConfig.initialState,
  );

  private readonly subscriptions$: Subscription[] = [];

  public isChatBtnDisabled = true;

  private relationRequestsTables = inject(RELATION_REQUESTS_TABLES);
  private searchTableFactory = inject(UserSearchTableConstructor);
  private tableFactory = inject(RelationRequestTableFactory<T>);
  private relationRequestService = inject(UserRelationRequestService<T>);
  private dialogModalContext = inject(RelationRequestDialogContextService);
  private operationService = inject(OperationInfoService);
  private chatSendMessageToService = inject(ChatSendMessageToService);
  private sessionService = inject(SessionService);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  public constructor() {
    this.isLoading$ = this.relationRequestService.isLoading$.pipe(shareReplay(1));
    this.totalTabItems$ = this.relationRequestService.totalRequests$.pipe(shareReplay(1));

    this.tableData$ = this.relationRequestService.relationRequestsList$.pipe(
      tap(({ totalCount }: RelationRequests<T>) => this.activeTab.updateCount(totalCount)),
      map(({ items }: RelationRequests<T>) => {
        if (this.pageConfig.allowedChat) {
          this.updateChatBtnStatus(items);
        }

        return this.buildTableMetaData(items);
      }),
    );

    this.searchTableData$ = this.relationRequestService.suggestionsUserList$.pipe(
      tap(({ totalCount }: SuggestedUsers) => this.activeTab.updateCount(totalCount)),
      map(({ items }: SuggestedUsers) => this.buildSearchTableMetaData(items)),
    );
  }

  public ngOnInit(): void {
    this.subscribeOnRouteChanges();
    this.subscribeOnParamsChanges();
    this.subscribeOnTablesActions();
    this.subsscribeOnRelationStatusChanges();

    this.dialogModalContext.modalContext = this.pageConfig.kind;

    this.subscriptions$.push(
      this.relationRequestService.relatedUsersList$
        .pipe(
          tap((users: UserBasic[]) => {
            const user: MaybeUndefined<UserBasic> = startedUserDialogue(users);

            if (user && this.sessionService.is2AuthLvlPassed) {
              this.continueDialogueAfter2Auth(user);
            }
          }),
        )
        .subscribe(),
    );
  }

  private updateChatBtnStatus(items: T[]): void {
    this.isChatBtnDisabled = items.every((request) => request.relatedUser.account_status === AccountStatus.DEACTIVATED);
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
    subjectsComplete([this.tableAction$, this.searchTableAction$]);
  }

  public get activeTabState$(): Observable<ListState<RelationPageTabs, K>> {
    return this.activeTab.state$;
  }

  public get sortParams$(): Observable<SortParams> {
    return this.activeTab.state$.pipe(
      map((activeTab: ListState<RelationPageTabs, K>) => sortParams[activeTab.sorting]),
    );
  }

  public get selectedFilter$(): Observable<MaybeNull<FilterOption<K>>> {
    return this.activeTab.state$.pipe(
      map(
        (activeTab: ListState<RelationPageTabs, K>) =>
          this.pageConfig.filter?.options.find((item: FilterOption<K>) => item.value === activeTab.filtering) ?? null,
      ),
    );
  }

  public onNavigateToMessagingPage(): void {
    const { pagination, sorting, filtering } = this.activeTab.snapshot;

    if (!this.sessionService.is2AuthLvlPassed) {
      this.authService.showSendMessageRefuseModal(this.router.url);
      return;
    }

    promiseWithCatch(
      this.router.navigate([this.LINK_GROUP_MESSAGE], {
        relativeTo: this.activatedRoute.parent,
        queryParams: {
          sorting,
          filtering,
          page: pagination.currentPage,
        },
      }),
    );
  }

  public onTabChanged({ nextId }: NgbNavChangeEvent<string>): void {
    promiseWithCatch(this.router.navigate([nextId], { relativeTo: this.activatedRoute.parent }));
  }

  public onPageChanged(currentPage: number): void {
    const { pagination } = this.activeTab;

    this.activeTab.patch({ pagination: { ...pagination, currentPage } });

    window.scroll(0, 0);
  }

  public onSortParamChanged(orderType: SortingOrderType): void {
    const { snapshot } = this.activeTab;

    this.activeTab.patch({ sorting: buildSorting(snapshot.sorting, orderType) });
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.activeTab.patch({ sorting: sortOptions.sortCategory });
  }

  public applyFilterByCategory(filterOptions: FilterOption<K>): void {
    this.activeTab.patch({ filtering: filterOptions.value });
  }

  public onKeywordChanged($event: KeyboardEvent & { target: HTMLInputElement }): void {
    const { snapshot } = this.activeTab;
    const { pagination, sorting, filtering, queryKeyword } = this.activeTab.initialState;

    const state: ListState<RelationPageTabs, K> = {
      ...snapshot,
      pagination,
      sorting,
      filtering,
      queryKeyword,
      mode: ListMode.SEARCH,
    };

    const keyword: string = $event.target.value.trim();

    if (snapshot.mode === this.ListMode.BROWSE && keyword) {
      this.activeTab.patch({ ...state, queryKeyword: keyword });
      return;
    }

    if (snapshot.mode === this.ListMode.SEARCH && keyword) {
      this.activeTab.patch({ ...snapshot, queryKeyword: keyword });
      return;
    }

    if (snapshot.mode === this.ListMode.SEARCH && !keyword) {
      const shouldReplaceSorting = snapshot.sorting === SortCategories.SCORE_DESC;
      const newSorting = shouldReplaceSorting ? SortCategories.NAME_ASC : snapshot.sorting;

      this.activeTab.patch({
        ...state,
        mode: this.ListMode.BROWSE,
        queryKeyword: '',
        sorting: newSorting,
      });

      return;
    }
  }

  public onTableAction(event: TableEvent): void {
    this.tableAction$.next(event);
  }

  public onSearchTableAction(event: TableEvent): void {
    this.searchTableAction$.next(event);
  }

  public applyDefaultParams(): void {
    const { pagination, sorting, filtering, queryKeyword, mode } = this.activeTab.initialState;

    let effectiveSorting: SortCategories = sorting;
    const isKeywordEmpty = !queryKeyword || String(queryKeyword).trim() === '';

    if (isKeywordEmpty && effectiveSorting === SortCategories.SCORE_DESC) {
      effectiveSorting = SortCategories.NAME_ASC;
    }

    this.activeTab.patch({
      mode,
      pagination,
      sorting: effectiveSorting,
      filtering,
      queryKeyword,
    });
  }

  private buildTableMetaData(items: T[]): UserTableConstructorInfo {
    const { snapshot } = this.activeTab;

    return this.tableFactory.buildTableConstructorData(this.relationRequestsTables[snapshot.context], items);
  }

  private buildSearchTableMetaData(items: SuggestedUser[]): UserTableConstructorInfo {
    return this.searchTableFactory.buildTableData(items);
  }

  private subscribeOnRouteChanges(): void {
    this.subscriptions$.push(
      this.activatedRoute.paramMap
        .pipe(map((params: ParamMap) => routeParamToTabId(params.get('type'), this.activeTab.initialState.context)))
        .subscribe((context: RelationPageTabs) => {
          const { pagination, filtering, queryKeyword, mode } = this.activeTab.initialState;

          this.activeTab.patch({
            context,
            pagination,
            filtering,
            mode,
            queryKeyword,
            sorting: sortingBy(context),
          });

          this.loadTotalRequests();
        }),
    );
  }

  private subscribeOnParamsChanges(): void {
    this.subscriptions$.push(
      this.activeTab.params$.subscribe(
        ({ context, pagination, sorting, filtering, queryKeyword, mode }: ListState<RelationPageTabs, K>) => {
          mode === this.ListMode.BROWSE
            ? this.loadRelationRequests(context, pagination, sorting, filtering)
            : this.loadSuggestionUsers({
                pagination,
                sortParams: sortParams[sorting],
                params: { queryText: queryKeyword, itemKind: SearchCategories.ALL_CATEGORIES },
              });
        },
      ),
    );
  }

  private subscribeOnTablesActions(): void {
    type TableAction = Record<
      CellActionTypes.REMOVE | CellActionTypes.REJECT | CellActionTypes.CONFIRM | CellActionTypes.SEND_MSG,
      (event: TableEvent, item: T) => void
    >;

    const tableActions: TableAction = {
      SEND_MSG: (event: TableEvent, item: T) => this.sendMessage(event, item.relatedUser),
      CONFIRM: (event: TableEvent, item: T) => this.acceptRelationRequest(event, item),
      REJECT: (event: TableEvent, item: T) => this.rejectRelationRequest(event, item),
      REMOVE: (event: TableEvent, item: T) =>
        item.acceptanceDate
          ? this.removeAcceptedRelationRequest(event, item)
          : this.removeSentRelationRequest(event, item),
    };

    const searchTableActions: SearchTableAction = {
      ADD: (event: TableEvent, item: SuggestedUser) => this.createRelationRequest(event, item),
      REMOVE: (event: TableEvent, item: SuggestedUser) => this.removeRelationByUser(event, item),
      SEND_MSG: (event: TableEvent, item: SuggestedUser) => this.sendMessage(event, item.user),
    };

    this.subscriptions$.push(
      this.tableAction$
        .pipe(
          filter(({ type }: TableEvent) => Object.keys(tableActions).includes(type)),
          withLatestFrom(this.relationRequestService.relationRequestsList$),
          map<[TableEvent, RelationRequests<T>], [TableEvent, T]>(
            ([event, tableData]: [TableEvent, RelationRequests<T>]) => {
              const item: MaybeUndefined<T> = tableData.items.find(
                (item: T) => item.relatedUser.user_id === event.actorId,
              );

              assertNonNullish(item);
              return [event, item];
            },
          ),
          tap(([event, item]: [TableEvent, T]) => {
            const action: (event: TableEvent, item: T) => void = objectValue(
              tableActions,
              event.type as AllowedTableEvent,
            );
            action(event, item);
          }),
        )
        .subscribe(),
    );

    this.subscriptions$.push(
      this.searchTableAction$
        .pipe(
          filter(({ type }: TableEvent) => Object.keys(searchTableActions).includes(type)),
          withLatestFrom(this.relationRequestService.suggestionsUserList$),
          map<[TableEvent, SuggestedUsers], [TableEvent, SuggestedUser]>(
            ([event, tableData]: [TableEvent, SuggestedUsers]) => {
              const user: MaybeUndefined<SuggestedUser> = tableData.items.find(
                (item: SuggestedUser) => item.user.user_id === event.actorId,
              );

              assertNonNullish(user);
              return [event, user];
            },
          ),
          tap(([event, item]: [TableEvent, SuggestedUser]) => {
            const action: (event: TableEvent, item: SuggestedUser) => void = objectValue(
              searchTableActions,
              event.type as AllowedSearchTableEvent,
            );
            action(event, item);
          }),
        )
        .subscribe(),
    );
  }

  private subsscribeOnRelationStatusChanges(): void {
    this.subscriptions$.push(
      this.relationRequestService.relationStatusChanges$
        .pipe(
          tap(() => {
            this.refreshList();
            this.loadTotalRequests();
          }),
        )
        .subscribe(),
    );
  }

  private loadRelationRequests(
    context: RelationPageTabs,
    pagination: PaginationState,
    sorting: SortCategories,
    filtering: K,
  ): void {
    type TabAction = Record<
      RelationPageTabs,
      (pagination: PaginationState, sorting: SortCategories, filtering: K) => void
    >;

    const tabAction: TabAction = {
      [RelationPageTabs.LIST]: () => this.relationRequestService.loadAcceptedRequest(pagination, sorting, filtering),
      [RelationPageTabs.REQUESTS_SENT]: () =>
        this.relationRequestService.loadSentRequest(pagination, sorting, filtering),
      [RelationPageTabs.REQUESTS_RECEIVED]: () =>
        this.relationRequestService.loadReceivedRequest(pagination, sorting, filtering),
    };

    tabAction[context](pagination, sorting, filtering);
  }

  private loadSuggestionUsers(state: SearchState): void {
    this.relationRequestService.loadSuggestionUsers(state);
  }

  private loadTotalRequests(): void {
    this.relationRequestService.loadRequestsTotalCount();
  }

  private refreshList(): void {
    const { pagination, snapshot } = this.activeTab;

    const isPageWithLastItem: boolean = // total - offset = 1
      pagination.totalItemsCount - (pagination.limit ?? this.PAGE_LIMIT) * (pagination.currentPage - 1) === 1;

    const isSearchMode: boolean = snapshot.mode === this.ListMode.SEARCH;

    if (pagination.currentPage !== 1 && !isSearchMode && isPageWithLastItem) {
      this.activeTab.updatePage(pagination.currentPage - 1);
    }

    this.activeTab.triggerParams();
  }

  private sendMessage(_: TableEvent, user: UserBasic): void {
    if (this.sessionService.is2AuthLvlPassed) {
      this.chatSendMessageToService.sendMessageToProfiles([user], true /* showConfirmation */);
    } else {
      localStorage.setItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID, user.user_id);
      this.authService.showSendMessageRefuseModal(this.router.url);
    }
  }

  private continueDialogueAfter2Auth(user: UserBasic): void {
    this.chatSendMessageToService.sendMessageToProfiles([user], true);
    localStorage.removeItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID);
  }

  private acceptRelationRequest(event: TableEvent, item: T): void {
    this.operationService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        successfulMessage: this.dialogModalContext.successfullAcceptInvitation,
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.REJECT, event) },
        ],
      },
      () => {
        this.relationRequestService.acceptRelationRequest(item);
      },
    );
  }

  private createRelationRequest(event: TableEvent, user: SuggestedUser): void {
    this.operationService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.ADD, event) },
        successfulMessage: this.dialogModalContext.successfullSent,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.relationRequestService.createRelationRequest(user);
      },
    );
  }

  private rejectRelationRequest(event: TableEvent, item: T): void {
    this.operationService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.REJECT, event) },
        successfulMessage: this.dialogModalContext.successfullReject,
        confirmationDialog: this.dialogModalContext.comfirmRejectInvitationRequest,
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        ],
      },
      () => {
        this.relationRequestService.rejectRelationRequest(item);
      },
    );
  }

  private removeSentRelationRequest(_: TableEvent, item: T): void {
    this.operationService.setupOperation(
      {
        successfulMessage: this.dialogModalContext.successfullRemoveSentRequest,
        confirmationDialog: this.dialogModalContext.comfirmRemovalSentRequest,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.relationRequestService.removeRelationRequest(item);
      },
    );
  }

  private removeAcceptedRelationRequest(_: TableEvent, item: T): void {
    this.operationService.setupOperation(
      {
        successfulMessage: this.dialogModalContext.successfullRemoveAcceptedRequest,
        confirmationDialog: this.dialogModalContext.comfirmRemovalAcceptedRequest,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.relationRequestService.removeRelationRequest(item);
      },
    );
  }

  private removeRelationByUser(_: TableEvent, user: SuggestedUser): void {
    this.operationService.setupOperation(
      {
        successfulMessage: this.dialogModalContext.successfullRemoveAcceptedRequest,
        confirmationDialog: this.dialogModalContext.comfirmRemovalAcceptedRequest,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.relationRequestService.removeRelationByUser(user);
      },
    );
  }
}
